import { Stack, TextField } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'

/**
 * Story 80 — the duration control of an external job's return timing, in DURATION mode.
 *
 * Three numeric fields instead of one free-text `hh:mm:ss` box. The user should not have to know the
 * format, nor type the colons: each field says what goes in it and carries the native stepper of
 * `type='number'`, the same pattern the sibling fields of this panel already use (`Operators`,
 * `Minimum quantity`).
 *
 * Why not a clock picker: the hours of a return are UNBOUNDED (`48:30:00` is a valid subcontracting
 * return, V5). Every picker in `@mui/x-date-pickers` clamps its hour section to 23 and carries a date
 * rather than a span, so none of them can represent this value at all.
 *
 * What travels out is still the persisted `hh:mm:ss` string, so the contract with Sun is untouched:
 * this component only changes how that string is typed.
 *
 * @param {Object} props
 * @param {string} props.value - Stored duration as `hh:mm:ss`; empty or absent when not declared
 * @param {boolean} props.disabled
 * @param {function(string)} props.onChange - Receives the recomposed `hh:mm:ss`, or '' when all blank
 */

const MAX_SEXAGESIMAL = 59

const pad = (raw) => String(raw === '' ? 0 : Number(raw)).padStart(2, '0')

const isAllBlank = (parts) => parts.hours === '' && parts.minutes === '' && parts.seconds === ''

/**
 * Recomposes by PADDING the parts, not by going through milliseconds. Milliseconds would carry 70
 * minutes over into an extra hour, rewriting a value the user never typed; padding keeps what is on
 * screen and what gets saved the same thing.
 *
 * All three blank composes to '' rather than '00:00:00'. That distinction is load bearing: '00:00:00'
 * is a declared "returns immediately" and satisfies the client guard, so collapsing blank into it
 * would let an external job with no declared timing through the save.
 */
const compose = (parts) => isAllBlank(parts)
  ? ''
  : pad(parts.hours) + ':' + pad(parts.minutes) + ':' + pad(parts.seconds)

/**
 * Splits the persisted string into the three raw field values.
 *
 * Deliberately keeps them as STRINGS and exactly as they arrive: a field must be able to hold '' (the
 * user cleared it) and must not silently rewrite a value the user has not touched. A string that is
 * not three parts long cannot be mapped onto the fields at all, so it degrades to empty and the error
 * below explains why.
 */
const splitDuration = (value) => {
  const parts = String(value ?? '').split(':')
  if (parts.length !== 3) {
    return { hours: '', minutes: '', seconds: '' }
  }
  return { hours: parts[0], minutes: parts[1], seconds: parts[2] }
}

/** A part is wrong when it is not a whole non-negative number, or when it overflows its own ceiling. */
const isPartInvalid = (raw, max) => {
  if (raw === '') {
    return false
  }
  const parsed = Number(raw)
  if (!Number.isInteger(parsed) || parsed < 0) {
    return true
  }
  return max !== undefined && parsed > max
}

const ReturnDurationField = ({ value, disabled, onChange }) => {
  const { t_ } = useSmartTranslation()
  // The raw field contents are local state, not derived from `value` on every render, for one reason:
  // a blank field has to survive. Derived, clearing hours would emit '00:30:00', the parent would echo
  // it back, and the field would refill itself with '00' — leaving the user no way to undeclare a
  // duration they had already typed.
  const [parts, setParts] = useState(() => splitDuration(value))

  // Re-sync only when `value` is NOT what these fields already say: that is a change from outside
  // (loading a workflow, reverting, switching mode), and it must win. When the parent is merely
  // echoing back what was just emitted, the blanks on screen are left alone.
  useEffect(() => {
    setParts((current) => compose(current) === (value ?? '') ? current : splitDuration(value))
  }, [value])

  // A value the three fields cannot express at all — a missing colon, say — arriving from Sun's
  // deliberately tolerant read (RN17: reading never fails). It is reported on its own, because the
  // per-field checks below have nothing to attach to when the split does not give three parts.
  // A three-part value is NOT flagged here even when out of range: the offending field flags itself,
  // which is what tells the user where to look.
  const unsplittable = Boolean(value) && String(value).split(':').length !== 3

  const invalid = {
    hours: isPartInvalid(parts.hours),
    minutes: isPartInvalid(parts.minutes, MAX_SEXAGESIMAL),
    seconds: isPartInvalid(parts.seconds, MAX_SEXAGESIMAL)
  }

  const updatePart = (key, raw) => {
    const nextParts = { ...parts, [key]: raw }
    setParts(nextParts)
    onChange(compose(nextParts))
  }

  const fields = [
    { key: 'hours', label: t_('Hours'), max: undefined },
    { key: 'minutes', label: t_('Minutes'), max: MAX_SEXAGESIMAL },
    { key: 'seconds', label: t_('Seconds'), max: MAX_SEXAGESIMAL }
  ]

  // The message is rendered once, under the first offending field: repeating it three times would be
  // noise, and the contract promises the user one explanation of the expected format.
  const firstOffender = unsplittable
    ? 'hours'
    : fields.map((field) => field.key).find((key) => invalid[key])

  return (
    <Stack direction='row' spacing={1} alignItems='flex-start'>
      {fields.map((field) => (
        <TextField
          key={field.key}
          label={field.label}
          type='number'
          value={parts[field.key]}
          disabled={disabled}
          size='small'
          sx={{ width: 80 }}
          inputProps={field.max === undefined ? { min: 0 } : { min: 0, max: field.max }}
          // Boolean, never the validator's message: passing a string here makes MUI emit
          // aria-invalid="<the message>", which is not a valid ARIA value.
          error={firstOffender === field.key || invalid[field.key]}
          helperText={firstOffender === field.key
            ? t_('Invalid return duration. Expected format: hh:mm:ss')
            : ''}
          onChange={(event) => updatePart(field.key, event.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      ))}
    </Stack>
  )
}

export default ReturnDurationField
